import { useEffect, useRef } from 'react'
import { useUser } from '@/contexts/UserContext'
import { useGuideContext } from '@/contexts/GuideContext'

interface UseGuideAutostartOptions {
  /** Selector CSS del anchor que debe existir en el DOM antes de arrancar. */
  anchorSelector: string
  /** Tiempo máximo de espera en ms antes de arrancar igual. Default 3000. */
  timeoutMs?: number
}

/**
 * Dispara startGuide() automáticamente en la PRIMERA visita al módulo:
 *
 * Condiciones para el auto-inicio:
 * 1. `user.tours_vistos[guideId]` es falsy (nunca fue marcada como vista).
 * 2. `GuideContext.pendingGuideId !== guideId` — si coincide, significa que
 *    el usuario llegó desde el panel de primeros pasos y `usePendingGuide`
 *    ya se encarga del arranque; NO debemos disparar ambos.
 * 3. Primer mount del componente (one-shot via firedRef).
 *
 * Mecanismo: rAF poll (igual que usePendingGuide) para esperar el anchor.
 * El auto-inicio se salta silenciosamente si el usuario ya vio la guía o si
 * está cargando la sesión.
 */
export function useGuideAutostart(
  guideId: string,
  startGuide: () => void,
  { anchorSelector, timeoutMs = 3000 }: UseGuideAutostartOptions,
): void {
  const { user, isLoading } = useUser()
  const { pendingGuideId } = useGuideContext()
  const firedRef = useRef(false)

  useEffect(() => {
    // Esperar a que la sesión esté resuelta antes de decidir.
    if (isLoading) return

    // Ya se disparó (one-shot).
    if (firedRef.current) return

    // Si la guía ya fue vista, no auto-arrancar.
    const alreadySeen = Boolean(user?.tours_vistos?.[guideId])
    if (alreadySeen) return

    // Si llegamos desde el panel (pendingGuideId coincide), usePendingGuide
    // ya manejará el arranque — no double-fire.
    if (pendingGuideId === guideId) return

    // Todo listo: poll rAF para anchor, luego arrancar.
    let rafId = 0
    const deadline = performance.now() + timeoutMs

    const tick = () => {
      if (document.querySelector(anchorSelector) || performance.now() >= deadline) {
        firedRef.current = true
        startGuide()
        return
      }
      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])
  // Dependencias intencionalmente solo [isLoading]:
  // - El efecto debe dispararse UNA SOLA VEZ al mount, en cuanto isLoading pasa a false.
  // - pendingGuideId, user, startGuide son leídos dentro del efecto pero no deben
  //   re-dispararlo; de lo contrario cada cambio de state en la página relanzaría
  //   el poll (y posiblemente la guía).
}
